import { gql } from '@apollo/client'
import { QueryNodeUtils, RuntimeOperations } from '@helo/cms'
import { asyncFlow, flow, tap } from '@helo/utils'

import { ReloadButton } from '../../../components/Toast/ReloadButton'
import { showToast } from '../../../components/Toast/showToast'
import {
  cmsConfigsQuery,
  schemaHashQuery,
} from '../../../queries/graphql/graphqlQueries'
import { apolloClient } from '../../../services/apolloClient'
import { AuthSlice } from '../auth/authSlice'
import { QuerySlice } from '../queryStore/querySlice'
import { RouterSlice } from '../router/routerSlice'
import { CmsSlice } from './cmsSlice'
import { CmsSliceParts } from './cmsSlice.parts'

// Poll fast while the electrumpython query field is still missing (db-subgraphs
// not ready yet), then back off once it exists.
const SCHEMA_WATCH_FAST_MS = 10_000
const SCHEMA_WATCH_SLOW_MS = 60_000
const SCHEMA_UPDATE_TOAST_ID = 'schema-update-available'
const ELECTRUM_PYTHON_QUERY_FIELD = 'electrumpython'

// Combined check: schema hash + introspection of the root Query fields so we can
// tell whether `electrumpython` is available yet, in a single request.
const schemaWatchQuery = gql`
  query SchemaWatch {
    schemaHash
    __type(name: "Query") {
      fields {
        name
      }
    }
  }
`

const fetchCurrentSchemaHash = async (): Promise<string | null> => {
  const { data } = await apolloClient.query({
    query: schemaHashQuery,
  })
  return data?.schemaHash ?? null
}

type SchemaWatchState = {
  schemaHash: string | null
  electrumPythonExists: boolean
}

const fetchSchemaWatchState = async (): Promise<SchemaWatchState> => {
  const { data } = await apolloClient.query<{
    schemaHash: string | null
    __type: { fields: Array<{ name: string }> } | null
  }>({ query: schemaWatchQuery })
  const fields = data?.__type?.fields ?? []
  return {
    schemaHash: data?.schemaHash ?? null,
    electrumPythonExists: fields.some(
      field => field.name === ELECTRUM_PYTHON_QUERY_FIELD,
    ),
  }
}

let schemaWatchTimeout: ReturnType<typeof setTimeout> | null = null
// null = not yet observed. Tracks whether `electrumpython` existed on the
// previous check so we can detect the missing -> present transition.
let previousElectrumPythonExists: boolean | null = null

const runSchemaWatchCheck = async (get: () => CmsSlice) => {
  // Default to the fast cadence if the check throws so we keep retrying while
  // the schema may still be settling.
  let electrumPythonExists = false
  try {
    const storedHash = get().cms.schemaHash
    const { schemaHash, electrumPythonExists: exists } =
      await fetchSchemaWatchState()
    electrumPythonExists = exists

    const hashChanged =
      !!schemaHash && storedHash !== null && schemaHash !== storedHash
    const electrumPythonAppeared =
      previousElectrumPythonExists === false && exists

    console.log('[SchemaWatch] check result', {
      storedHash,
      fetchedHash: schemaHash,
      hashChanged,
      previousElectrumPythonExists,
      electrumPythonExists: exists,
      electrumPythonAppeared,
    })

    previousElectrumPythonExists = exists

    // electrumpython went from missing -> present: the app now has the types it
    // needs, so reload automatically to pick them up instead of prompting.
    if (electrumPythonAppeared) {
      window.location.reload()
      return
    }

    if (hashChanged) {
      showToast({
        id: SCHEMA_UPDATE_TOAST_ID,
        title: 'App updates are available, please reload.',
        duration: null,
        isClosable: true,
        children: ReloadButton({
          onClick: () => window.location.reload(),
        }),
      })
    }
  } catch (error) {
    // ponytail: interval check is best-effort; page setup still catches changes on navigate
    console.log('[SchemaWatch] check errored', error)
  } finally {
    schemaWatchTimeout = setTimeout(
      () => void runSchemaWatchCheck(get),
      electrumPythonExists ? SCHEMA_WATCH_SLOW_MS : SCHEMA_WATCH_FAST_MS,
    )
  }
}

const startSchemaWatch = (get: () => CmsSlice) => {
  if (schemaWatchTimeout) {
    clearTimeout(schemaWatchTimeout)
  }
  // Start on the fast cadence until we've confirmed electrumpython exists.
  schemaWatchTimeout = setTimeout(
    () => void runSchemaWatchCheck(get),
    SCHEMA_WATCH_FAST_MS,
  )
}

const hasSchemaChanged = () =>
  asyncFlow(
    asyncFlow.inputExtends<CmsSlice>(),
    async (store: CmsSlice): Promise<boolean> => {
      if (store.cms.schemaHash === null) {
        return false
      }

      try {
        const hash = await fetchCurrentSchemaHash()
        if (!hash) {
          return false
        }
        return hash !== store.cms.schemaHash
      } catch {
        return false
      }
    },
  )

const checkSchemaHashOnNavigate = (get: () => CmsSlice) =>
  tap((store: CmsSlice) => {
    void (async () => {
      const storedHash = get().cms.schemaHash
      if (storedHash === null) {
        return
      }

      try {
        const hash = await fetchCurrentSchemaHash()
        if (!hash || hash === storedHash) {
          return
        }
        window.location.reload()
      } catch {
        // ponytail: navigate check is best-effort; schema watch still prompts while idle
      }
    })()
  })

const initializeCms = (
  get: () => CmsSlice & QuerySlice & RouterSlice & AuthSlice,
) =>
  flow(
    flow.inputExtends<CmsSlice & QuerySlice & RouterSlice & AuthSlice>(),
    store => {
      // Default context for @helo/cms RuntimeOperations (query store, router, URL params).
      RuntimeOperations.setDefaultContext({
        query: (queryId: string) => {
          const query = get().queryStore.getQuery(queryId)

          return query
        },
        queryVariables: (queryId: string) => {
          const queryNode = get().queryStore.getQuery(queryId)

          if (!queryNode) return null
          return QueryNodeUtils.getMappedVariablesAsObject()(queryNode)
        },
        urlParams(key?: string) {
          const urlParams = get().router.urlParams || {}

          if (key) {
            return urlParams[key]
          }
          return null
        },
        route: (routeId: string) => {
          return get().router.getRouteById(routeId)
        },
      })

      return store
    },
  )

const retrieveCmsConfigs = () =>
  asyncFlow(
    asyncFlow.inputExtends<CmsSlice>(),
    async (store: CmsSlice): Promise<CmsSlice> => {
      const { data } = await apolloClient.query({
        query: cmsConfigsQuery,
      })
      return {
        ...store,
        cms: {
          ...store.cms,
          ...(data ?? {}),
          schemaHash: data?.schemaHash ?? store.cms.schemaHash,
        },
      }
    },
    CmsSliceParts.setupDataTypeConfigs(),
  )

export const CmsSliceActions = {
  getQueryDefinition: CmsSliceParts.getQueryDefinition,
  setupDataTypeConfigs: CmsSliceParts.setupDataTypeConfigs,
  retrieveCmsConfigs,
  initializeCms,
  hasSchemaChanged,
  checkSchemaHashOnNavigate,
  startSchemaWatch,
}
